import * as _ from 'underscore';
import { BaseUIElement, UIElement } from '../uiElements';
import { Icons } from '../../../const';
import { Templates, Localization } from '../../../compile';
import { Event } from '../../../models';
import { ISerializable, IEvent, IKeyValue } from '../../../interfaces';
import { CommandOptions, FormOptions } from '../../../interfaces/options';
import { DefaultFormOptions } from '../../../const/defaultOptions';

export class BaseForm implements ISerializable<IKeyValue<any>> {
	private label: KnockoutObservable<string>;
	private uiElements: KnockoutObservableArray<BaseUIElement>;
	private clickSubmit: () => void;
	private onClickedSubmit = new Event<IKeyValue<any>>();
	private externalValidation: any[];
	public template: KnockoutObservable<string>;
	private commands: CommandOptions[];

	public get ClickedSubmit(): IEvent<IKeyValue<any>> {
		return this.onClickedSubmit;
	}

	constructor(options: FormOptions = <FormOptions>{}) {
		_.defaults(options, DefaultFormOptions);
		this.template = ko.observable<string>(options.template);
		this.label = ko.observable<string>(options.label);
		this.uiElements = ko.observableArray<BaseUIElement>();

		this.commands = options.commands || [];
		this.commands.forEach(
			command => (command.visible = _.isFunction(command.visible) ? command.visible : () => true)
		);

		this.onClickedSubmit = new Event<IKeyValue<any>>();

		this.clickSubmit = () => {
			this.submit();
		};

		this.externalValidation = [];

		if (options.initDefaultCommands) {
			this.commands.push({
				label: Localization.Save,
				css: Icons.Submit,
				command: this.clickSubmit,
				template: Templates.forms_action,
				visible: () => true
			});
		}
	}

	public submit() {
		if (this.validate()) {
			this.onClickedSubmit.notify(this.serialize());
		}
	}

	public addCommand(options: CommandOptions) {
		options.visible = _.isFunction(options.visible) ? options.visible : () => true;
		this.commands.unshift(options);
	}

	public addUIElement<T>(uiElement: BaseUIElement): BaseUIElement {
		this.uiElements.push(uiElement);
		return uiElement;
	}

	public addUIElementBefore<T>(uiElement: BaseUIElement, id: string): BaseUIElement {
		let index = _.findIndex(this.uiElements(), { id: id });
		this.uiElements.splice(index, 0, uiElement);
		return uiElement;
	}

	public addUIElementAfter<T>(uiElement: BaseUIElement, id: string): BaseUIElement {
		let index = _.findIndex(this.uiElements(), { id: id });
		this.uiElements.splice(index + 1, 0, uiElement);
		return uiElement;
	}

	public setUIElements(uiElements: BaseUIElement[]) {
		this.uiElements(uiElements);
	}

	public serialize(): IKeyValue<any> {
		var serializeObject: IKeyValue<any> = {};
		var elements = this.uiElements();
		elements.forEach(element => {
			if (element instanceof UIElement) {
				element.serialize(serializeObject);
			}
		});

		return serializeObject;
	}

	public setLabel(label: string) {
		this.label(label);
	}

	public validate(): boolean {
		var isValid = true;
		var validationGroup = ko.validation.group(this.getValidationGroup(), { deep: true });
		if (validationGroup().length > 0) {
			isValid = false;
			validationGroup.showAllMessages(true);
		}
		return isValid;
	}

	public attachToValidationGroup(obj: any[]) {
		this.externalValidation = this.externalValidation.concat(obj);
	}

	public hideValidationMessages() {
		var validationGroup = ko.validation.group(this.getValidationGroup(), { deep: true });
		validationGroup.showAllMessages(false);
	}

	public setVisibility(elementIds: string[], visible: boolean = true) {
		this.uiElements().forEach(uiElement => {
			if (_.contains(elementIds, uiElement.id)) {
				uiElement.visible(visible);
				elementIds = _.without(elementIds, uiElement.id);
			}
		});
	}

	public setEditable(elementIds: string[], editable: boolean = true) {
		this.uiElements().forEach(uiElement => {
			if (uiElement instanceof UIElement && _.contains(elementIds, uiElement.id)) {
				uiElement.editable(editable);
				elementIds = _.without(elementIds, uiElement.id);
			}
		});
	}

	public resetValues(elementIds: string[]) {
		this.uiElements().forEach(uiElement => {
			if (_.contains(elementIds, uiElement.id)) {
				if (uiElement instanceof UIElement) {
					uiElement.value(null);
				}
			}
		});
	}

	public resetAllValues() {
		this.uiElements().forEach(uiElement => {
			if (uiElement instanceof UIElement) {
				uiElement.value(null);
			}
		});
	}

	public getUIElement(id: string): BaseUIElement {
		return _.findWhere(this.uiElements(), { id: id });
	}

	public replaceUIElement(oldId: string, newUIElement: BaseUIElement) {
		var oldUIElement = this.getUIElement(oldId);
		this.uiElements.replace(oldUIElement, newUIElement);
	}

	public removeUIElement<T extends BaseUIElement>(id: string | T) {
		if (typeof id === 'string') {
			this.uiElements.remove(this.getUIElement(id));
		} else if (id instanceof BaseUIElement) {
			this.uiElements.remove(id);
		}
	}

	public removeAllUIElements() {
		this.uiElements([]);
	}
	public focus() {
		let elements = this.uiElements();
		if (elements && elements.length > 0) {
			elements.every((value: BaseUIElement, index: number) => {
				if (value instanceof UIElement) {
					value.hasFocus(true);
					return false;
				}
				return true;
			});
		}
	}
	private getValidationGroup(): any {
		return this.externalValidation.length > 0 ? this.externalValidation.concat(this) : this;
	}
}
